import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@mantine/core';
import AppStack from '../../common/AppStack';
import AppCheckbox from '../../common/AppCheckbox';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';
import { ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM } from '../../../config/constants';

const PrintDesignEditorCanvasItemPropertiesSection = ({
  canvasController,
  selectedObject,
  loading
}) => {
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    message: '',
    title: ''
  });
  const { setItemProperties } = canvasController;

  const onPropertiesChange = (newProperties) => {
    setItemProperties(selectedObject.id, {
      ...selectedObject,
      itemProperties: {
        ...selectedObject.itemProperties,
        ...newProperties
      }
    });
  };

  return (
    selectedObject && (
      <AppStack style={{ gap: 16, marginTop: 8 }}>
        {selectedObject.fkEcomPrintDesignItemType ===
        ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT ? (
          <>
            <AppStack style={{ marginBottom: 10 }}>
              <Select
                data={[
                  {
                    label: 'None',
                    value: 'none'
                  },
                  {
                    label: 'Uppercase',
                    value: 'uppercase'
                  },
                  {
                    label: 'Lowercase',
                    value: 'lowercase'
                  },
                  {
                    label: 'Capitalize',
                    value: 'capitalize'
                  }
                ]}
                description="Transform the text to uppercase, lowercase, or capitalize each word."
                disabled={loading}
                label="Text transform"
                onChange={(value) => {
                  onPropertiesChange({ textTransform: value });
                }}
                value={selectedObject.itemProperties?.textTransform || 'none'}
              />
            </AppStack>

            <AppCheckbox
              checked={selectedObject.itemProperties?.allowMultiline !== false}
              description="Long text wraps at spaces onto extra lines so it can still fill the height of the box. Turn this off to disable automatic wrapping - text shrinks to fit its line instead. Line breaks typed into the text are always kept."
              disabled={loading}
              label="Let this text wrap to multiple lines"
              onChange={() => {
                onPropertiesChange({
                  allowMultiline:
                    selectedObject.itemProperties?.allowMultiline === false
                });
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <AppCheckbox
              checked={selectedObject.itemProperties?.isRequired || false}
              description="Users must fill out this field when purchasing this product."
              disabled={loading}
              label="Make input required"
              onChange={() => {
                onPropertiesChange({isRequired: !selectedObject.itemProperties?.isRequired});
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <AppCheckbox
              checked={selectedObject.itemProperties?.noLetters || false}
              description="Users cannot enter letters for this field."
              disabled={loading}
              label="No letters"
              onChange={() => {
                const hasLetters = /\p{L}/u.test(selectedObject.text || '');
                if (!selectedObject.itemProperties?.noLetters && hasLetters) {
                  setConfirmModalState({
                    isOpen: true,
                    title: 'Remove letters from input?',
                    message: 'This will remove all letters from the input.',
                    onConfirm: () => {
                      onPropertiesChange({ noLetters: true });
                    }
                  });
                }
                else {
                  onPropertiesChange({noLetters: !selectedObject.itemProperties?.noLetters});
                }
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <AppCheckbox
              checked={selectedObject.itemProperties?.noNumbers || false}
              description="Users cannot enter numbers for this field."
              disabled={loading}
              label="No numbers"
              onChange={() => {
                const hasNumbers = /\p{N}/u.test(selectedObject.text || '');
                if (!selectedObject.itemProperties?.noNumbers && hasNumbers) {
                  setConfirmModalState({
                    isOpen: true,
                    title: 'Remove numbers from input?',
                    message: 'This will remove all numbers from the input.',
                    onConfirm: () => {
                      onPropertiesChange({ noNumbers: true });
                    }
                  });
                }
                else {
                  onPropertiesChange({noNumbers: !selectedObject.itemProperties?.noNumbers});
                }
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <AppCheckbox
              checked={
                selectedObject.itemProperties?.noSpecialCharacters || false
              }
              description="Users cannot enter special characters for this field."
              disabled={loading}
              label="No special characters"
              onChange={() => {
                const hasSpecialCharacters = /[^\p{L}\p{N}\s]/u.test(
                  selectedObject.text || ''
                );
                if (
                  !selectedObject.itemProperties?.noSpecialCharacters &&
                  hasSpecialCharacters
                ) {
                  setConfirmModalState({
                    isOpen: true,
                    title: 'Remove special characters from input?',
                    message:
                      'This will remove all special characters from the input.',
                    onConfirm: () => {
                      onPropertiesChange({
                        noSpecialCharacters: !selectedObject.itemProperties
                          ?.noSpecialCharacters
                      });
                    }
                  });
                }
                else {
                  onPropertiesChange({
                    noSpecialCharacters: !selectedObject.itemProperties
                      ?.noSpecialCharacters
                  });
                }
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <AppCheckbox
              checked={selectedObject.itemProperties?.noSpaces || false}
              description="Users cannot enter spaces for this field."
              disabled={loading}
              label="No spaces"
              onChange={() => {
                const hasSpaces = /\s/.test(selectedObject.text || '');
                if (!selectedObject.itemProperties?.noSpaces && hasSpaces) {
                  setConfirmModalState({
                    isOpen: true,
                    title: 'Remove spaces from input?',
                    message: 'This will remove all spaces from the input.',
                    onConfirm: () => {
                      onPropertiesChange({noSpaces: !selectedObject.itemProperties?.noSpaces});
                    }
                  });
                }
                else {
                  onPropertiesChange({noSpaces: !selectedObject.itemProperties?.noSpaces});
                }
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <ConfirmModal
              isOpen={confirmModalState.isOpen}
              message={
                <AppStack style={{ gap: 8 }}>
                  <AppText style={{ fontSize: 14 }}>
                    {confirmModalState.message}
                  </AppText>

                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Do you want to continue?
                  </AppText>
                </AppStack>
              }
              onCancel={() => {
                setConfirmModalState({
                  ...confirmModalState,
                  isOpen: false
                });
              }}
              onConfirm={() => {
                if (confirmModalState.onConfirm) {
                  confirmModalState.onConfirm();
                }
                setConfirmModalState({
                  ...confirmModalState,
                  isOpen: false
                });
              }}
              title={confirmModalState.title}
            />
          </>
        ) : null}
      </AppStack>
    )
  );
};

PrintDesignEditorCanvasItemPropertiesSection.propTypes = {
  canvasController: PropTypes.object,
  loading: PropTypes.bool,
  selectedObject: PropTypes.object
};

export default PrintDesignEditorCanvasItemPropertiesSection;
